import { useEffect, useState } from 'react';
import currencyParser from '@/utils/currencyParser';
import Chip from '@/components/Layout/Chip';
import Analysis from '../../../assets/svg/ProductChips/analysis.svg';
import Overview from '../../../assets/svg/ProductChips/overview.svg';
import News from '../../../assets/svg/ProductChips/news.svg';
import Deals from '../../../assets/svg/ProductChips/deals.svg';
import ChipHamburger from '../../../assets/svg/ProductChips/chipHamburger.svg';

import * as S from './styles';
import classNames from '@/utils/className';
import Icon from '@/components/Layout/Icon';

interface ProductChipsProps {
  analysis?: string;
  hasDeals: boolean;
  hasOverview: boolean;
  hasNews: boolean;
  sponsoredChip?: boolean;
  minorValue: {
    lojaId: string;
    titulo: string;
    url: string;
    encurtador: string;
    imagem: string;
    preco: number;
    parcelado?: string;
    loja: string;
    logo: string;
    produto: string;
  };
}
export default function ProductChips({
  analysis = undefined,
  hasDeals = false,
  hasOverview = false,
  hasNews = false,
  minorValue,
  sponsoredChip = false,
}: ProductChipsProps) {
  const ChipValues = [
    { key: 'review', icon: <Analysis />, label: 'review', visible: analysis !== undefined, link: analysis },
    { key: 'ficha-tecnica', icon: <Overview />, label: 'ficha técnica', visible: hasOverview, link: '#ficha-tecnica' },
    { key: 'noticias', icon: <News />, label: 'notícias', visible: true, link: '#noticias' },
    { key: 'ofertas', icon: <Deals />, label: 'preços', visible: hasDeals, link: '#precos' },
  ];
  const [selectedChip, setSelectedChip] = useState(ChipValues.find(e => e.visible)?.key);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const currentChip = ChipValues.find(values => values.key === selectedChip);

  const backgroundChipUrl = 'https://t.ctcdn.com.br/QFdnjPQOAnsY-aHSkABkXT-BcN4=/i1068821.jpeg';

  useEffect(() => {
    setSelectedChip(ChipValues.find(e => e.visible)?.key);
  }, [analysis, hasDeals, hasNews, hasOverview]);

  return (
    <>
      <div className="relative mb-8 flex flex-wrap gap-4 [&>div:last>*]:hidden tablet:[&>div:last>*]:flex [&>div>*]:flex [&>div>*]:max-w-[153px] [&>div>*]:gap-x-2 [&>div>*]:rounded-[100px] [&>div>*]:px-12 [&>div>*]:py-3">
        {analysis && (
          <S.ChipParent role="tab" tabIndex={0}>
            <Chip
              chipSize="large"
              prepIcon={<Analysis />}
              color="black"
              borderColor="gray"
              backgroundColor="white"
              texto="review"
              link={analysis}
              hover="gray"
            />
          </S.ChipParent>
        )}
        {hasOverview && (
          <S.ChipParent
            role="tab"
            tabIndex={0}
            onKeyPress={event => {
              event.preventDefault();
              if (event.key === 'enter') setSelectedChip('ficha-tecnica');
            }}
            selected={selectedChip === 'ficha-tecnica'}
            onClick={() => selectedChip !== 'ficha-tecnica' && setSelectedChip('ficha-tecnica')}>
            <Chip
              chipSize="large"
              prepIcon={<Overview />}
              color="black"
              borderColor="gray"
              backgroundColor="white"
              texto="ficha técnica"
              link="#ficha-tecnica"
              hover="gray"
            />
          </S.ChipParent>
        )}
        {hasNews && (
          <S.ChipParent
            role="tab"
            tabIndex={0}
            onKeyPress={event => {
              event.preventDefault();
              if (event.key === 'enter') setSelectedChip('noticias');
            }}
            selected={selectedChip === 'noticias'}
            onClick={() => selectedChip !== 'noticias' && setSelectedChip('noticias')}>
            <Chip
              chipSize="large"
              prepIcon={<News />}
              color="black"
              borderColor="gray"
              backgroundColor="white"
              texto="notícias"
              link="#noticias"
              hover="gray"
            />
          </S.ChipParent>
        )}
        {hasDeals && (
          <S.ChipParent
            role="tab"
            tabIndex={0}
            onKeyPress={event => {
              event.preventDefault();
              if (event.key === 'enter') setSelectedChip('ofertas');
            }}
            selected={selectedChip === 'ofertas'}
            onClick={() => selectedChip !== 'ofertas' && setSelectedChip('ofertas')}>
            <Chip chipSize="large" prepIcon={<Deals />} color="black" borderColor="gray" backgroundColor="white" texto="preços" link="#precos" hover="gray" />
          </S.ChipParent>
        )}
        {hasDeals && (
          <S.ChipParent greenBackground>
            <Chip
              chipSize="large"
              color="white"
              backgroundColor="green"
              borderColor="green"
              highlightedText={currencyParser(minorValue.preco)}
              texto="melhor preço"
              posIcon={<Icon icon="mdi:open-in-new" fontSize="0.75rem" />}
              link={minorValue.encurtador}
            />
          </S.ChipParent>
        )}
        {sponsoredChip && (
          <S.ChipParent>
            <Chip
              chipSize="large"
              link="https://ofertas.canaltech.com.br/marca/oppo/"
              target="_blank"
              backgroundImage={backgroundChipUrl}
              texto="OFERTAS OPPO"
              posIcon={<Icon icon="mdi:open-in-new" fontSize="0.75rem" />}
            />
          </S.ChipParent>
        )}
      </div>
      <div className="relative mb-4 flex flex-row items-center gap-x-4 rounded-[100px] bg-mglGray300 px-5 py-3 text-mglGray900 no-underline transition-all duration-300 ease-ease md:mb-8 tablet:hidden [&>span>svg>path]:fill-mglGray900">
        <span className="relative flex">{currentChip && currentChip.icon}</span>
        <span className="relative mt-[-2px] whitespace-nowrap text-center text-base no-underline">{currentChip && currentChip.label}</span>
        <span className="relative ml-auto flex" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <ChipHamburger />
        </span>
        <ul
          className={classNames(
            'shadow-[0px 2px 5px 0px rgba(0, 0, 0, 0.3)] absolute left-0 top-0 z-50 w-full flex-col gap-y-2 rounded-xl bg-mglGray50 transition-all duration-300 ease-ease',
            S.variants.ChipMenuDropdown.isOpen[isDropdownOpen.toString()]
          )}>
          {ChipValues.map(
            value =>
              value.visible && (
                <a href={value.link} key={value.key}>
                  <li
                    className={classNames(
                      'relative mt-1 flex items-center gap-x-4 rounded-[100px] border border-solid border-mglGray200 bg-mglGray100 px-5 py-3 text-mglGray900 no-underline transition-all duration-300 ease-ease hover:bg-mglGray200 hover:text-mglGray900 [&>span>svg>path]:fill-mglGray900 [&>span>svg>path]:hover:fill-mglGray900 [&>span>svg]:w-[19px]',
                      S.variants.ChipMenuDropdownItem.selected[(selectedChip === value.key).toString()]
                    )}
                    key={value.key}
                    onClick={() => {
                      setSelectedChip(value.key);
                      setIsDropdownOpen(false);
                    }}>
                    <span className="relative flex">{value.icon}</span>
                    {value.label}
                  </li>
                </a>
              )
          )}
        </ul>
      </div>
      {sponsoredChip && (
        <Chip
          chipSize="large"
          backgroundImage={backgroundChipUrl}
          className="md:hidden"
          texto="OFERTAS OPPO"
          link="https://ofertas.canaltech.com.br/"
          target="_blank"
          posIcon={<Icon icon="mdi:open-in-new" fontSize="0.75rem" />}
        />
      )}
    </>
  );
}
